import { Typography, CircularProgress, Stack, Button } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import Page from '../components/Page';
import moment from 'moment';
import { Icon } from '@iconify/react';
import { DataGrid, GridToolbarContainer, GridToolbarExport } from '@mui/x-data-grid';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import plusFill from '@iconify/icons-eva/plus-fill';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { capitalize } from 'lodash';
import { useRecoilState, useRecoilValue } from 'recoil';
import { snackbarAtom } from 'src/atoms/snackbar';
import { userAtom } from 'src/atoms/user';
import { downloadAllReports } from 'src/services/employee.service';
import axios from 'axios';

const columns = [
  //   { field: 'id', headerName: 'Id', width: 150, type: 'string' },
  {
    field: 'name',
    headerName: 'Name',
    width: 250,
    type: 'string'
  },
  {
    field: 'age',
    headerName: 'Age',
    width: 150,
    type: 'number'
  },
  {
    field: 'gender',
    headerName: 'Gender',
    width: 150,
    type: 'singleSelect',
    valueOptions: ['Male', 'Female']
  },
  {
    field: 'active',
    headerName: 'Active',
    width: 150,
    type: 'boolean'
  },
  {
    field: 'marital_status',
    headerName: 'Marital Status',
    width: 200,
    type: 'singleSelect',
    valueOptions: ['Single', 'Married', 'Divorced']
  },
  {
    field: 'email',
    headerName: 'Email',
    width: 200,
    type: 'string'
  },
  {
    field: 'role',
    headerName: 'Role',
    width: 200,
    type: 'singleSelect',
    valueOptions: ['Admin', 'Finance', 'Doctor', 'Employee']
  },
  {
    field: 'beneficiaries_count',
    headerName: 'Beneficiaries Count',
    width: 250,
    type: 'number'
  },
  {
    field: 'dental',
    headerName: 'Dental Balance (EGP)',
    width: 250,
    type: 'number'
  },
  {
    field: 'glasses',
    headerName: 'Glasses Balance (EGP)',
    width: 240,
    type: 'number'
  },
  {
    field: 'total_balance',
    headerName: 'Total  (EGP)',
    width: 200,
    type: 'number'
  }
];
const loaderStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%,-50%)',
  height: '10%',
  width: '10%',
  display: 'grid',
  placeItems: 'center center'
};

function CustomToolbar() {
  return (
    <GridToolbarContainer>
      <GridToolbarExport />
    </GridToolbarContainer>
  );
}

const Employees = () => {
  /* eslint-disable no-unused-vars */
  const [snackbar, setSnackbar] = useRecoilState(snackbarAtom);
  const user = useRecoilValue(userAtom);
  const [exporting, setExporting] = useState(false);
  const theme = useTheme();
  const match = useMediaQuery(theme.breakpoints.down('lg'));
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [filterModel, setFilterModel] = useState({
    items: JSON.parse(localStorage.getItem('employeesFilterModel')) || []
  });
  const [pageSize, setPageSize] = useState(Number(localStorage.getItem('employeesPageSize')) || 10);
  const employees = data?.map((emp) => {
    return {
      id: emp.id,
      name: emp.name,
      role: capitalize(emp.role),
      gender: capitalize(emp.gender),
      active: emp.active,
      email: emp.email,
      age: moment().diff(emp.birthdate, 'years', false),
      beneficiaries_count: emp.beneficiaries.length,
      marital_status: capitalize(emp.marital_status),
      dental: emp.balance?.total_glasses_balance ?? 0,
      glasses: emp.balance?.total_glasses_balance ?? 0,
      total_balance: emp.ceiling_limit?.total_balance ?? 0
    };
  });
  useEffect(() => {
    setLoading(true);
    async function getData() {
      try {
        const { data, status } = await axios('/employees');
        if (status !== 200) {
          throw new Error("Couldn't fetch employees data");
        }
        setData([...data]);
        setLoading(false);
      } catch (err) {
        setSnackbar({ severity: 'error', open: true, message: err.message });
        console.log(err);
      }
    }
    getData();
  }, []);

  if (loading) {
    return <CircularProgress style={loaderStyle} />;
  }
  return (
    <Page title="Employees | Incorta" style={{ paddingInline: match ? '16px' : '0px' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
        <Typography variant="h4" gutterBottom>
          Employees
        </Typography>

        <Stack direction="row" gap={2}>
          {user?.role === 'ADMIN' && (
            <Button
              variant="outlined"
              disabled={exporting}
              onClick={async () => {
                try {
                  setExporting(true);
                  await downloadAllReports();
                } catch (err) {
                  setSnackbar({ severity: 'error', open: true, message: err.message });
                } finally {
                  setExporting(false);
                }
              }}
            >
              {exporting ? 'Exporting…' : 'Export all (Excel)'}
            </Button>
          )}
          <Button
            variant="contained"
            component={RouterLink}
            to="/dashboard/employees/new"
            startIcon={<Icon icon={plusFill} />}
          >
            New Employee
          </Button>
        </Stack>
      </Stack>
      <DataGrid
        rows={employees}
        autoHeight
        filterModel={filterModel}
        onFilterModelChange={(model) => {
          localStorage.setItem('employeesFilterModel', JSON.stringify(model.items));
          setFilterModel({
            items: model.items
          });
        }}
        columns={columns}
        onRowClick={(e) => {
          navigate(`./${e.id}`);
        }}
        pageSize={pageSize}
        onPageSizeChange={(newSize) => {
          localStorage.setItem('employeesPageSize', newSize);
          setPageSize(newSize);
        }}
        rowsPerPageOptions={[5, 10, 15, 20]}
        components={{
          Toolbar: CustomToolbar
        }}
        disableSelectionOnClick
      />
    </Page>
  );
};
export default Employees;
